import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import jsQR from 'jsqr';
import styles from '../../styles.module.css';

const QRScanner = ({ open, onClose, onScan }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(document.createElement('canvas'));
    const [error, setError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);

    useEffect(() => {
        let stream = null;
        let animationFrameId = null;

        const startCamera = async () => {
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }, // Prefer rear camera
                });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                    setIsScanning(true);
                    scanQRCode();
                }
            } catch (err) {
                setError('Failed to access camera. Please ensure camera permissions are granted.');
                console.error('Camera error:', err);
            }
        };

        const scanQRCode = () => {
            if (!videoRef.current || !isScanning) return;

            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            if (video.videoWidth && video.videoHeight) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'dontInvert',
                });

                if (code) {
                    onScan(code.data); // Call callback with QR code data
                    stopCamera();
                    return;
                }
            }

            animationFrameId = requestAnimationFrame(scanQRCode);
        };

        const stopCamera = () => {
            setIsScanning(false);
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            onClose(); // Close the modal
        };

        if (open) {
            startCamera();
        }

        return () => {
            stopCamera();
        };
    }, [open, onScan, onClose]);

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 2,
                    borderRadius: 2,
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 2 }}>
                    <Typography variant="h6">Scan QR Code</Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {error ? (
                    <Typography color="error">{error}</Typography>
                ) : (
                    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
                        <video
                            ref={videoRef}
                            style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'cover' }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                border: '2px solid red',
                                borderRadius: 2,
                                pointerEvents: 'none',
                                opacity: 0.5,
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Modal>
    );
};

export default QRScanner;